/**
 * VerifyEmail: two-stage "add email" verification page, rendered at /verifyEmail
 * (ProtectedRoute, requiresLogin=true). Stage 1 collects an email (debounced
 * validation via utils/user validateEmail) and requests a code through api/user
 * (UserAPI.submitEmail); stage 2 verifies the 6-digit code (UserAPI.submitCode),
 * stores the updated user in contexts/UserContext, and navigates to /account.
 * "Unauthorized" errors redirect to /signUpLogin; others surface via
 * contexts/ErrorContext.
 */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { setUserContext, useUserDispatch } from "@/contexts/UserContext";
import { UserAPI } from "@/api/user";
import { validateEmail } from "@/utils/user";
import { useError } from "@/contexts/ErrorContext";

type Stage = "email" | "code";

const VerifyEmail: React.FC<{}> = () => {
    const [stage, setStage] = useState<Stage>("email");
    const [email, setEmail] = useState<string>("");
    const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
    const [code, setCode] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const { showError } = useError();
    const userDispatch = useUserDispatch();
    const navigate = useNavigate();
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const debounce = (func: Function, delay: number) => {
        return (...args: any) => {
            clearTimeout(timerRef.current as NodeJS.Timeout);
            timerRef.current = setTimeout(() => func(...args), delay);
        };
    };

    const handleEmailSubmit = async () => {
        setLoading(true);
        try {
            await UserAPI.submitEmail(email);
            setStage("code");
        } catch (errMessage) {
            if (errMessage === "Unauthorized") {
                navigate("/signUpLogin");
            }
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const handleCodeSubmit = async () => {
        setLoading(true);
        try {
            const { user } = await UserAPI.submitCode(email, code);
            if (user) {
                setUserContext(userDispatch, user);
                navigate("/account");
            } else {
                console.error("User should not be null");
                showError("an unexpected error occurred. please try again.");
            }
        } catch (errMessage) {
            if (errMessage === "Unauthorized") {
                navigate("/signUpLogin");
            }
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const debouncedValidateEmail = debounce(async (newEmail: string) => {
        const isValid = validateEmail(newEmail);
        setIsValidEmail(isValid);
        if (newEmail.length <= 8) {
            return;
        }
        if (!isValid) {
            showError("invalid email address");
            return;
        }
    }, 800);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        debouncedValidateEmail(newEmail);
    };

    const inputClasses =
        "w-48 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:border-black focus:outline-none font-porsui tracking-widest ";
    const buttonClasses =
        "w-48 flex justify-center py-1.5 px-3 text-sm font-medium text-black bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-porsui ";

    return (
        <div className="bg-slate-50 flex flex-col items-center justify-center w-full h-full">
            <div className="max-w-md w-full flex flex-col items-center space-y-8 p-8">
                <div className="text-center">
                    <h2 className="text-2xl text-black font-porsui font-bold">add email</h2>
                </div>

                <div className="w-48 mt-8 space-y-6 relative flex flex-col items-center">
                    {/* Email input */}
                    <div
                        className={`transition-opacity duration-500 ${
                            stage !== "email"
                                ? "opacity-0 invisible absolute"
                                : "opacity-100 visible"
                        }`}
                    >
                        <input
                            type="email"
                            id="email"
                            className={inputClasses + "text-center text-sm w-64"}
                            value={email}
                            onChange={handleEmailChange}
                            disabled={loading || stage !== "email"}
                        />
                        <div className="mt-2 text-xs text-zinc-500 font-porsui flex flex-col items-center space-y-0.5">
                            <span>will never be shared or public</span>
                        </div>
                    </div>

                    {/* Code input */}
                    <div
                        className={`w-48 transition-opacity duration-500 ${
                            stage !== "code"
                                ? "opacity-0 invisible absolute"
                                : "opacity-100 visible"
                        }`}
                    >
                        <input
                            type="text"
                            id="code"
                            className={inputClasses + "text-center"}
                            maxLength={6}
                            placeholder="enter code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            disabled={loading || stage !== "code"}
                        />
                    </div>

                    {/* Submit button */}
                    <button
                        onClick={() => {
                            if (stage === "email") handleEmailSubmit();
                            else if (stage === "code") handleCodeSubmit();
                        }}
                        disabled={loading || (stage === "email" && !isValidEmail)}
                        className={buttonClasses}
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4" />
                        ) : stage === "email" ? (
                            "send code"
                        ) : (
                            "submit code"
                        )}
                    </button>

                    {/* Resend code button for code stage */}
                    {stage === "code" && (
                        <button
                            onClick={handleEmailSubmit}
                            disabled={loading}
                            className="mt-2 text-zinc-500 hover:text-zinc-600 focus:outline-none text-sm font-porsui"
                        >
                            resend code
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
